<svelte:options customElement={{ tag: "bt-curation-reminders-panel" }} />

<script lang="ts">
  import { onMount } from 'svelte';
  import { fade, fly } from 'svelte/transition';
  import { cubicOut } from 'svelte/easing';
  import { modalStore } from '$shared/modal/modalStore';
  import ModalToggle from '$shared/modal-toggle/ModalToggle.svelte';
  import Icon from '$shared/icons/Icon.svelte';
  import { eventBus } from '$shared/lib/eventBus';

  export let panelTitle: string = 'Set email reminders for your curation';
  export let recipientsLabel: string = 'Recipients:';
  export let recipientsText: string = '';
  export let recipientsOrgText: string = '';
  export let recipientsGroupTemplate: string = 'Everyone in %{group_name}';
  export let sentEmails: string = '[]';
  export let pendingPublishedEmail: string = '';
  export let pendingAssignedEmail: string = '';
  export let addReminderText: string = 'Add another reminder';
  export let addFirstReminderText: string = 'Add email reminder';
  export let emptyStateText: string = 'Schedule email reminders to engage learners with this curation.';
  export let featuredContentOptions: string = '[]';
  export let savedReminders: string = '[]';
  export let saveRemindersUrl: string = '';
  export let addSubjectLineText: string = 'Add subject line (required)';
  export let addNoteText: string = 'Add a note (required)';
  export let selectFeaturedContentText: string = 'Select featured content (optional)';
  export let setSendDateText: string = 'Set send date';
  export let pastDateErrorText: string = 'Send date must be in the future.';
  export let deleteReminderText: string = 'Delete reminder';
  export let previewEmailPrefix: string = 'Preview email template on our';
  export let previewEmailLinkText: string = 'help center';
  export let cancelText: string = 'Cancel';
  export let saveText: string = 'Save';
  export let savedToastText: string = 'Email reminders successfully scheduled.';
  export let setRemindersText: string = 'Set reminders';
  export let editRemindersText: string = 'Edit reminders';
  export let deliverable: string = 'true';
  export let panelTitleSet: string = 'Set email reminders for your curation';
  export let disabledNoticeText: string = 'Publish or assign your curation to enable email reminders again.';

  // Re-publish/re-assign makes panel editable again without a reload
  let deliverableOverride: boolean | null = null;
  $: canSend = deliverableOverride ?? deliverable === 'true';

  let titleOverride: string | null = null;
  $: displayedTitle = titleOverride ?? panelTitle;

  interface SentEmail {
    kind?: string;
    sentText: string;
    subject: string;
    note: string;
    featuredContent?: string;
  }
  interface PendingEmail {
    kind: string;
    sentTextTemplate: string;
    subject: string;
    note: string;
  }
  interface FeaturedContentOption {
    id: number;
    title: string;
    type: string;
  }
  interface ReminderRow {
    uid: number;
    id?: number;
    subject: string;
    note: string;
    sendDate: string;
    featuredContentId: string;
  }

  $: modalName = $modalStore.modalName;

  let sentEmailsList: SentEmail[] = [];
  $: sentEmailsList = JSON.parse(sentEmails || '[]');

  let publishedEmailJustSent = false;
  let optimisticAssignedEmails: SentEmail[] = [];
  let recipientsOverride: string | null = null;
  $: displayedRecipientsText = recipientsOverride ?? recipientsText;

  onMount(() => {
    eventBus.on('curation-published-email-sent', () => {
      publishedEmailJustSent = true;
    });
    eventBus.on('curation-published', () => {
      recipientsOverride = recipientsOrgText || null;
      markDeliverable();
    });
    eventBus.on('curation-assigned', ({ groupName }) => {
      recipientsOverride = recipientsGroupTemplate.replace('%{group_name}', groupName);
      markDeliverable();
    });
    eventBus.on('curation-assigned-email-sent', ({ groupName }) => {
      if (!pendingAssignEntry) return;
      const recipients = recipientsGroupTemplate.replace('%{group_name}', groupName);
      optimisticAssignedEmails = [
        ...optimisticAssignedEmails,
        {
          kind: pendingAssignEntry.kind,
          sentText: pendingAssignEntry.sentTextTemplate
            .replace('%{date}', formatDate(new Date()))
            .replace('%{recipients}', recipients),
          subject: pendingAssignEntry.subject,
          note: pendingAssignEntry.note
        }
      ];
    });
    eventBus.on('curation-unassigned', () => {
      recipientsOverride = recipientsOrgText || null;
    });
  });

  // Re-enable the panel without a reload: cancelled reminders aren't editable, so the
  // title resets to "set" and the trigger button follows any remaining editable rows.
  function markDeliverable() {
    deliverableOverride = true;
    titleOverride = panelTitleSet;
    updateRemindersButton(reminders.length > 0);
  }

  $: pendingEntry = pendingPublishedEmail ? (JSON.parse(pendingPublishedEmail) as PendingEmail) : null;
  $: pendingAssignEntry = pendingAssignedEmail ? (JSON.parse(pendingAssignedEmail) as PendingEmail) : null;

  let featuredContentList: FeaturedContentOption[] = [];
  $: featuredContentList = JSON.parse(featuredContentOptions || '[]');

  let openDropdownUid: number | null = null;

  function toggleFeaturedDropdown(uid: number) {
    openDropdownUid = openDropdownUid === uid ? null : uid;
  }

  function closeFeaturedDropdown() {
    openDropdownUid = null;
  }

  function selectFeaturedContent(reminder: ReminderRow, option: FeaturedContentOption) {
    reminder.featuredContentId = String(option.id);
    reminders = reminders;
    openDropdownUid = null;
  }

  function featuredContentLabel(id: string) {
    const option = featuredContentList.find((candidate) => String(candidate.id) === id);
    return option ? `(${option.type}) ${option.title}` : '';
  }

  let nextUid = 1;

  // Builds editable rows from saved reminders (client uid + DB id for update-vs-create).
  function rowsFromSaved(json: string): ReminderRow[] {
    const rows = JSON.parse(json || '[]') as Array<{
      id: number;
      subject: string;
      note: string;
      sendDate: string;
      featuredContentId: string;
    }>;
    return rows.map((row) => ({
      uid: nextUid++,
      id: row.id,
      subject: row.subject,
      note: row.note,
      sendDate: row.sendDate,
      featuredContentId: row.featuredContentId ?? ''
    }));
  }

  let reminders: ReminderRow[] = rowsFromSaved(savedReminders);

  function addReminder() {
    reminders = [...reminders, { uid: nextUid++, subject: '', note: '', sendDate: '', featuredContentId: '' }];
  }

  function removeReminder(uid: number) {
    reminders = reminders.filter((reminder) => reminder.uid !== uid);
  }

  const csrfToken =
    document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

  let saving = false;
  let saveError = '';
  // Comparable signature of a row set (ignores client uid) for change detection.
  function remindersSignature(rows: Array<Partial<ReminderRow>>): string {
    return JSON.stringify(
      rows.map((r) => ({
        id: r.id ?? null,
        subject: (r.subject ?? '').trim(),
        note: (r.note ?? '').trim(),
        sendDate: r.sendDate ?? '',
        featuredContentId: r.featuredContentId || ''
      }))
    );
  }

  // Save enables only when deliverable, with complete rows AND a change vs. the last saved set.
  $: canSave =
    canSend &&
    reminders.every((row) => row.subject.trim() && row.note.trim() && row.sendDate && !isDatePast(row.sendDate)) &&
    remindersSignature(reminders) !== remindersSignature(JSON.parse(savedReminders || '[]'));

  // Sends the current rows to the server; on success refreshes the saved set and closes.
  async function saveReminders() {
    if (saving) return;
    saving = true;
    saveError = '';
    try {
      const payload = {
        reminders: reminders.map((row) => ({
          id: row.id,
          subject: row.subject,
          note: row.note,
          send_date: row.sendDate,
          featured_content_id: row.featuredContentId || null
        }))
      };
      const resp = await fetch(saveRemindersUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken,
          Accept: 'application/json'
        },
        credentials: 'same-origin',
        body: JSON.stringify(payload)
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        saveError = data.error || 'Could not save reminders.';
        return;
      }
      savedReminders = JSON.stringify(data.reminders ?? []);
      updateRemindersButton(data.remindersExist ?? false);
      eventBus.emit('flash-trigger', { message: savedToastText, type: 'success' });
      modalStore.setName('');
    } catch {
      saveError = 'Could not save reminders.';
    } finally {
      saving = false;
    }
  }

  // Sync the server-rendered Set/Edit reminders trigger button after a save.
  function updateRemindersButton(exists: boolean) {
    const btn = document.getElementById('curation-reminders-button');
    if (!btn) return;
    btn.dataset.remindersExist = String(exists);
    btn.textContent = exists ? editRemindersText : setRemindersText;
  }

  // Closing the panel discards unsaved edits, restoring the last-saved set.
  let panelWasOpen = false;
  $: {
    const panelOpen = modalName === 'curationReminders';
    if (panelWasOpen && !panelOpen) {
      reminders = rowsFromSaved(savedReminders);
      openDropdownUid = null;
    }
    panelWasOpen = panelOpen;
  }

  function openDatePicker(event: MouseEvent) {
    const input = event.currentTarget as HTMLInputElement;
    try {
      input.showPicker?.();
    } catch {
      input.focus();
    }
  }

  function formatDate(date: Date) {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  function formatIsoDate(isoDate: string) {
    const [year, month, day] = isoDate.split('-').map(Number);
    return formatDate(new Date(year, month - 1, day));
  }

  // Sends go out at 10am Eastern on the chosen day, so the earliest valid date is tomorrow.
  function minSendDate() {
    const date = new Date();
    date.setDate(date.getDate() + 1);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  // A chosen date is invalid when it's today or earlier (ISO strings compare lexically).
  function isDatePast(sendDate: string): boolean {
    return !!sendDate && sendDate < minSendDate();
  }

  // Show the email we just sent, since this list only refreshes on page reload.
  // Skip if one's already listed — re-publishing doesn't email anyone twice.
  $: displayedPublishedEmail =
    publishedEmailJustSent && pendingEntry && !sentEmailsList.some((email) => email.kind === 'published')
      ? [
          {
            kind: pendingEntry.kind,
            sentText: pendingEntry.sentTextTemplate.replace('%{date}', formatDate(new Date())),
            subject: pendingEntry.subject,
            note: pendingEntry.note
          }
        ]
      : [];

  $: displayedEmails = [...sentEmailsList, ...displayedPublishedEmail, ...optimisticAssignedEmails];

  function closeModal() {
    modalStore.setName('');
  }
</script>

<svelte:window on:click={closeFeaturedDropdown} />

{#if modalName === 'curationReminders'}
  <div class="panel__outer" transition:fade={{ duration: 250 }}>
    <ModalToggle closesModal modalToToggle={modalName} design="fullscreen"></ModalToggle>
    <section class="panel" transition:fly={{ x: 692, opacity: 1, duration: 250, easing: cubicOut }}>
      <header class="panel__header">
        <h2 class="panel__title">{displayedTitle}</h2>
        <ModalToggle
          closesModal
          modalToToggle={modalName}
          design="action-button-text"
          buttonClasses="panel__close-x"
        >
          <Icon icon="X2" size="medium" />
        </ModalToggle>
      </header>
      {#if canSend}
        <p class="panel__recipients">
          <span class="panel__recipients-label">{recipientsLabel}</span>
          <span>{displayedRecipientsText}</span>
        </p>
      {:else}
        <p class="panel__notice">{disabledNoticeText}</p>
      {/if}
      <div class="panel__scroll">
        {#if displayedEmails.length === 0 && reminders.length === 0}
          <div class="empty-state">
            <span class="empty-state__icon"><Icon icon="Mails" size="auto" /></span>
            <p class="empty-state__copy">{emptyStateText}</p>
            <button type="button" class="add-reminder" on:click={addReminder} disabled={!canSend}>
              <Icon icon="Plus" size="small" />
              {addFirstReminderText}
            </button>
          </div>
        {:else}
          {#each displayedEmails as sentEmail}
            <div class="sent-email">
              <p class="sent-email__meta">{sentEmail.sentText}</p>
              <div class="sent-email__fields">
                <div class="sent-email__subject">{sentEmail.subject}</div>
                <div class="sent-email__note">{sentEmail.note}</div>
                {#if sentEmail.featuredContent}
                  <div class="sent-email__featured">{sentEmail.featuredContent}</div>
                {/if}
              </div>
            </div>
          {/each}
          {#each reminders as reminder (reminder.uid)}
            <div class="reminder-row">
              <div class="reminder-row__fields">
                <input
                  type="text"
                  class="reminder-row__subject"
                  placeholder={addSubjectLineText}
                  bind:value={reminder.subject}
                />
                <textarea
                  class="reminder-row__note"
                  placeholder={addNoteText}
                  bind:value={reminder.note}
                ></textarea>
                <div class="reminder-row__dropdown">
                  <button
                    type="button"
                    class="reminder-row__dropdown-trigger"
                    class:open={openDropdownUid === reminder.uid}
                    on:click|stopPropagation={() => toggleFeaturedDropdown(reminder.uid)}
                  >
                    <span class="reminder-row__dropdown-label" class:placeholder={!reminder.featuredContentId}>
                      {reminder.featuredContentId ? featuredContentLabel(reminder.featuredContentId) : selectFeaturedContentText}
                    </span>
                    <span class="reminder-row__dropdown-icon" class:flipped={openDropdownUid === reminder.uid}>
                      <Icon icon="ChevronDown" size="medium" />
                    </span>
                  </button>
                  {#if openDropdownUid === reminder.uid}
                    <!-- svelte-ignore a11y-no-noninteractive-element-interactions a11y-click-events-have-key-events -->
                    <ul class="reminder-row__dropdown-menu" on:click|stopPropagation>
                      {#each featuredContentList as option}
                        <li>
                          <button
                            type="button"
                            class="reminder-row__dropdown-item"
                            on:click={() => selectFeaturedContent(reminder, option)}
                          >
                            ({option.type}) {option.title}
                          </button>
                        </li>
                      {/each}
                    </ul>
                  {/if}
                </div>
              </div>
              <div class="reminder-row__ctas">
                <div class="reminder-row__date-wrap">
                  <button type="button" class="reminder-row__date-btn">
                    <Icon icon="Calendar" size="small" />
                    {reminder.sendDate ? formatIsoDate(reminder.sendDate) : setSendDateText}
                  </button>
                  <input
                    type="date"
                    class="reminder-row__date-input"
                    min={minSendDate()}
                    bind:value={reminder.sendDate}
                    aria-label={setSendDateText}
                    on:click={openDatePicker}
                  />
                </div>
                <button type="button" class="reminder-row__delete" on:click={() => removeReminder(reminder.uid)}>
                  <Icon icon="Trash2" size="small" />
                  {deleteReminderText}
                </button>
              </div>
              {#if isDatePast(reminder.sendDate)}
                <p class="reminder-row__error" role="alert">{pastDateErrorText}</p>
              {/if}
            </div>
          {/each}
          <button type="button" class="add-reminder" on:click={addReminder} disabled={!canSend}>
            <Icon icon="Plus" size="small" />
            {addReminderText}
          </button>
        {/if}
      </div>
      {#if saveError}
        <p class="panel__error" role="alert">{saveError}</p>
      {/if}
      <footer class="panel__footer">
        <p class="panel__preview-note">
          {previewEmailPrefix} <a href="https://help.bigthink.com/help/set-email-reminders-for-a-curation" target="_blank" class="panel__preview-link">{previewEmailLinkText}</a>.
        </p>
        <button type="button" class="panel__cancel" on:click={closeModal}>{cancelText}</button>
        <button type="button" class="panel__save" on:click={saveReminders} disabled={saving || !canSave}>{saveText}</button>
      </footer>
    </section>
  </div>
{/if}

<style lang="postcss">
  /* Shadow DOM: Tailwind's global border-box reset doesn't reach in here */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
  .panel__outer {
    position: fixed;
    width: 100%;
    height: 100%;
    z-index: 50;
    top: 0;
    left: 0;
  }
  .panel {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: 692px;
    max-width: 100vw;
    display: flex;
    flex-direction: column;
    gap: theme('spacing.6');
    padding: theme('padding.6');
    background: white;
    box-shadow: theme('btpShadow.main-shadow');
  }
  .panel__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: theme('spacing.4');
  }
  .panel__title {
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-weight: 600;
    font-size: theme('fontSize.2xl');
    line-height: 30px;
    margin: 0;
  }
  :global(.panel__close-x) {
    position: absolute;
    top: theme('padding.4');
    right: theme('padding.6');
    width: theme('spacing.10');
    height: theme('spacing.10');
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 !important;
    margin: 0;
  }
  .panel__recipients {
    display: flex;
    gap: theme('spacing.2');
    margin: 0;
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    font-weight: 400;
  }
  .panel__recipients-label {
    font-weight: 500;
  }
  .panel__notice {
    margin: 0;
    color: theme('colors.neutral.500');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    font-weight: 400;
  }
  .panel__scroll {
    flex: 1 0 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: theme('spacing.6');
    padding: theme('padding.6') 0;
    border-top: 1px solid theme('colors.neutral.200');
    border-bottom: 1px solid theme('colors.neutral.200');
    overflow-y: auto;
  }
  .sent-email {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.2');
    width: 100%;
    flex-shrink: 0;
    padding: theme('padding.4');
    background: theme('colors.neutral.100');
    border: 1px solid theme('colors.neutral.200');
    border-radius: theme('borderRadius.lg');
  }
  .sent-email__meta {
    margin: 0;
    color: theme('colors.neutral.600');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    font-weight: 400;
  }
  .sent-email__fields {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.2');
    width: 100%;
  }
  .sent-email__subject,
  .sent-email__note,
  .sent-email__featured {
    width: 100%;
    padding: theme('padding.2');
    background: theme('colors.neutral.50');
    border: 1px solid theme('colors.neutral.200');
    border-radius: theme('borderRadius.lg');
    color: theme('colors.neutral.300');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
  }
  .sent-email__subject,
  .sent-email__featured {
    height: theme('spacing.10');
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .sent-email__note {
    height: 110px;
    overflow-y: auto;
  }
  .empty-state {
    flex: 1 0 0;
    min-height: 0;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: theme('spacing.6');
    padding: 0 theme('spacing.40');
  }
  .empty-state__icon {
    color: theme('colors.neutral.300');
  }
  .empty-state__icon :global(svg) {
    width: 123.5px;
    height: 123.5px;
  }
  .empty-state__copy {
    margin: 0;
    color: theme('colors.neutral.600');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    font-weight: 400;
    text-align: center;
  }
  .reminder-row {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.2');
    width: 100%;
    flex-shrink: 0;
    padding: theme('padding.4');
    background: theme('colors.neutral.50');
    border-radius: theme('borderRadius.lg');
  }
  .reminder-row__fields {
    display: flex;
    flex-direction: column;
    gap: theme('spacing.2');
    width: 100%;
  }
  .reminder-row__subject,
  .reminder-row__note {
    width: 100%;
    padding: theme('padding.2');
    background: white;
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    &::placeholder {
      color: theme('colors.neutral.500');
    }
    &:focus {
      border-color: theme('colors.black');
      outline: none;
    }
  }
  .reminder-row__subject {
    height: theme('spacing.10');
  }
  .reminder-row__note {
    height: 110px;
    resize: none;
    overflow-y: auto;
  }
  .reminder-row__dropdown {
    position: relative;
    width: 100%;
  }
  .reminder-row__dropdown-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: theme('spacing.2');
    width: 100%;
    padding: theme('padding.2');
    background: white;
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    text-align: left;
    cursor: pointer;
    &.open {
      border-color: theme('colors.black');
    }
  }
  .reminder-row__dropdown-label {
    flex: 1 0 0;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    &.placeholder {
      color: theme('colors.neutral.500');
    }
  }
  .reminder-row__dropdown-icon {
    display: flex;
    color: theme('colors.black');
    &.flipped {
      transform: scaleY(-1);
    }
  }
  .reminder-row__dropdown-menu {
    position: absolute;
    top: calc(100% + theme('spacing.1'));
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: theme('spacing.4');
    /* 5 items visible: 5 lines x 24px + 4 gaps x 16px + 32px padding */
    max-height: 216px;
    overflow-y: auto;
    margin: 0;
    padding: theme('padding.4');
    list-style: none;
    background: white;
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    box-shadow: theme('btpShadow.main-shadow');
  }
  .reminder-row__dropdown-item {
    display: block;
    width: 100%;
    padding: 0;
    margin: 0;
    background: none;
    border: none;
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    text-align: left;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .reminder-row__ctas {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .reminder-row__error {
    margin: 0;
    color: #b42318;
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    line-height: 21px;
  }
  .reminder-row__date-wrap {
    position: relative;
  }
  .reminder-row__date-btn {
    display: inline-flex;
    align-items: center;
    gap: theme('spacing.2');
    height: theme('spacing.10');
    padding: 0 theme('padding.4');
    background: white;
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    line-height: 21px;
    font-weight: 500;
    cursor: pointer;
  }
  .reminder-row__date-input {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
  }
  .reminder-row__delete {
    display: inline-flex;
    align-items: center;
    gap: theme('spacing.2');
    height: theme('spacing.10');
    padding: 0 theme('padding.4');
    background: transparent;
    border: none;
    border-radius: theme('borderRadius.lg');
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    line-height: 21px;
    font-weight: 500;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .add-reminder {
    display: inline-flex;
    align-items: center;
    gap: theme('spacing.2');
    height: theme('spacing.10');
    flex-shrink: 0;
    padding: 0 theme('padding.4');
    background: white;
    border: 1px solid theme('colors.neutral.300');
    border-radius: theme('borderRadius.lg');
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    line-height: 21px;
    font-weight: 500;
    cursor: pointer;
  }
  .add-reminder:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .panel__error {
    margin: 0 0 theme('spacing.3');
    color: #b42318;
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    text-align: right;
  }
  .panel__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: theme('spacing.6');
  }
  .panel__preview-note {
    flex: 1 0 0;
    min-width: 0;
    margin: 0;
    color: theme('colors.neutral.600');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.base');
    line-height: theme('lineHeight.6');
    font-weight: 400;
  }
  .panel__preview-link {
    color: theme('colors.neutral.600');
    text-decoration: underline;
  }
  .panel__cancel {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: theme('spacing.8');
    padding: 0 theme('padding.3');
    background: transparent;
    border: none;
    border-radius: theme('borderRadius.lg');
    color: theme('colors.black');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    line-height: 21px;
    font-weight: 500;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
  }
  .panel__save {
    display: inline-flex;
    align-items: center;
    gap: theme('spacing.2');
    height: theme('spacing.10');
    padding: 0 theme('padding.4');
    background: theme('colors.black');
    color: theme('colors.white');
    border: none;
    border-radius: theme('borderRadius.lg');
    font-family: theme('fontFamily.sans');
    font-size: theme('fontSize.sm');
    line-height: 21px;
    font-weight: 500;
    cursor: pointer;
    &:disabled {
      background: theme('colors.neutral.200');
      color: theme('colors.neutral.300');
      cursor: not-allowed;
    }
  }
</style>
